/**
 * 引导页：让这台设备先有一个冰箱。
 *
 * 三种入口，按用户实际会遇到的情况排：
 *   · 新建一个冰箱 —— 第一次来的人
 *   · 输入邀请码   —— 别人发来 6 位数字的人（这是主路径）
 *   · 站点口令     —— 只有部署方开了 APP_TOKEN 才会出现
 *
 * 为什么把口令做成这一页的一部分而不是单独一屏：
 * 一个既没口令又没冰箱的人会连着遇到两道门，分成两屏就是两次"这是什么"。
 * 摊在一屏里、并且明确说"口令是给整站用的，邀请码是给冰箱用的"，
 * 他才分得清这两样东西。
 */

import { HOUSEHOLD_NAME_MAX, formatInviteCode, isCompleteInviteCode, normalizeInviteCode } from '@fridge/shared';
import { useEffect, useState } from 'react';

import {
  ApiError,
  createHousehold,
  getToken,
  joinHousehold,
  setToken,
  UnauthError,
} from '../api/client';
import { GLYPHS, IconGlyph } from '../components/IconGlyph';
import { Pressable } from '../components/Pressable';
import { SegmentedControl } from '../components/SegmentedControl';
import { useToast } from '../components/Toast';
import { deviceLabel } from '../lib/device';
import { useAppData } from '../state/app-context';

type Mode = 'create' | 'join';

export function Onboarding(): JSX.Element {
  const { onboarding, completeOnboarding, refreshAll, loading, authRequired, households } =
    useAppData();
  const toast = useToast();

  const [mode, setMode] = useState<Mode>('create');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [token, setTokenValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameMax = onboarding?.nameMax ?? HOUSEHOLD_NAME_MAX;
  const needToken = (onboarding?.authEnabled ?? false) && getToken().length === 0;

  useEffect(() => {
    if (needToken) setTokenValue(getToken());
  }, [needToken]);

  /**
   * 已经有冰箱、只是口令没填（比如部署方后来才把口令门打开）。
   *
   * 这种情况最该做的只有一件事：填口令。把"新建 / 加入冰箱"摆在他面前
   * 是误导 —— 他不缺冰箱，他只是回不去自己那个。
   */
  if (authRequired && households.length > 0) {
    return (
      <TokenOnly
        token={token}
        onToken={setTokenValue}
        busy={busy}
        error={error}
        onRetry={() => {
          // 让用户能清掉填错的口令重新来
          setTokenValue('');
          setToken('');
          setError(null);
        }}
        onSubmit={() => {
          void (async () => {
            setBusy(true);
            setError(null);
            setToken(token.trim());
            await refreshAll();
            setBusy(false);
          })();
        }}
      />
    );
  }

  const canSubmit =
    mode === 'create' ? name.trim().length > 0 : isCompleteInviteCode(code);

  async function submit(): Promise<void> {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);

    // 先落口令：紧接着的请求会自动带上它
    if (needToken) setToken(token.trim());

    try {
      const session =
        mode === 'create'
          ? await createHousehold(name.trim(), deviceLabel())
          : await joinHousehold(code, deviceLabel());
      await completeOnboarding(session);
      toast.show(
        mode === 'create' ? `「${session.household.name}」建好了。` : `进了「${session.household.name}」。`,
        'success',
      );
    } catch (err) {
      if (err instanceof UnauthError) {
        setError('站点口令不对，再核对一下。');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('没能连上服务器，检查一下网络。');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col justify-center overflow-y-auto px-6 py-10">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-card bg-surface shadow-e1">
          <IconGlyph glyph={GLYPHS.fridge} size={28} className="text-accent" weight="fill" />
        </div>

        <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.02em] text-label">
          先给它弄个冰箱
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-label-2">
          一个冰箱就是一份独立的食材记录。同一份冰箱可以由几个人一起维护，
          各自的手机各记一把钥匙。一台设备也可以同时进好几个冰箱。
        </p>

        <div className="mt-6">
          <SegmentedControl<Mode>
            options={[
              { value: 'create', label: '新建一个' },
              { value: 'join', label: '我有邀请码' },
            ]}
            value={mode}
            onChange={(next) => {
              setMode(next);
              setError(null);
            }}
          />
        </div>

        <div className="mt-4 flex flex-col gap-4">
          {mode === 'create' ? (
            <div>
              <label className="field-label" htmlFor="hh-name">
                给它起个名字
              </label>
              <input
                id="hh-name"
                className="field"
                maxLength={nameMax}
                placeholder="我家的冰箱"
                value={name}
                autoFocus
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void submit();
                }}
              />
              <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
                名字随便起，之后在设置页还能改。建好之后会给你一枚 6 位邀请码，
                发给他，他就能一起维护这个冰箱。
              </p>
            </div>
          ) : (
            <div>
              <label className="field-label" htmlFor="hh-code">
                6 位邀请码
              </label>
              <input
                id="hh-code"
                className="field tnum text-center text-[24px] tracking-[0.3em]"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={formatInviteCode(code)}
                onChange={(event) => setCode(normalizeInviteCode(event.target.value))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void submit();
                }}
              />
              <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
                让对方在他手机的设置页里找到这个码。有效期 24 小时，用一次就作废。
              </p>
            </div>
          )}

          {needToken ? (
            <div>
              <label className="field-label" htmlFor="hh-token">
                站点口令
              </label>
              <input
                id="hh-token"
                className="field"
                type="password"
                autoComplete="current-password"
                placeholder="部署这个站的人设的那串口令"
                value={token}
                onChange={(event) => setTokenValue(event.target.value)}
              />
              <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
                这一条和上面那种不是一个东西：口令是整站的门，邀请码是某一个冰箱的门。
              </p>
            </div>
          ) : null}

          {error ? (
            <p className="text-[12.5px] leading-relaxed text-danger">{error}</p>
          ) : null}

          <Pressable
            className="cta w-full"
            disabled={!canSubmit || busy || loading}
            onClick={() => void submit()}
          >
            {busy ? (mode === 'create' ? '正在建' : '正在进') : mode === 'create' ? '建好，进去' : '进去'}
          </Pressable>

          <Pressable
            className="cta-quiet w-full"
            disabled={busy}
            onClick={() => void refreshAll()}
          >
            <IconGlyph glyph={GLYPHS.refresh} size={14} />
            重新载入
          </Pressable>
        </div>

        <p className="mt-5 text-[11.5px] leading-relaxed text-label-3">
          钥匙只存在这台设备的浏览器里。换手机时用新的邀请码进来就行 ——
          数据在冰箱里，不会因为换设备而丢。
          <br />
          <br />
          如果这台设备明明进过、现在却空了：Safari 会清理"没加到主屏"的网站的本地数据
          （大约 7 天没打开就会清）。找一枚新邀请码重新进来就好，东西一样不少。
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * 只差口令的情况。
 * 独立成一屏而不是复用上面那个表单：这一屏只有一件事可做，
 * 多给一个"新建冰箱"的按钮只会让人怀疑自己是不是点错了地方。
 */
function TokenOnly({
  token,
  onToken,
  busy,
  error,
  onSubmit,
  onRetry,
}: {
  token: string;
  onToken: (value: string) => void;
  busy: boolean;
  error: string | null;
  onSubmit: () => void;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col justify-center px-6 pb-24">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-card bg-surface shadow-e1">
          <IconGlyph glyph={GLYPHS.key} size={28} className="text-accent" weight="fill" />
        </div>

        <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.02em] text-label">
          需要站点口令
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-label-2">
          这个站设了一道口令门。填一次就记住了，之后不用再填。
          你的冰箱和里面的东西都还在。
        </p>

        <div className="mt-6">
          <label className="field-label" htmlFor="only-token">
            站点口令
          </label>
          <input
            id="only-token"
            className="field"
            type="password"
            autoComplete="current-password"
            placeholder="部署这个站的人设的那串"
            value={token}
            autoFocus
            onChange={(event) => onToken(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSubmit();
            }}
          />
        </div>

        {error ? <p className="mt-3 text-[12.5px] leading-relaxed text-danger">{error}</p> : null}

        <Pressable className="cta mt-4 w-full" disabled={busy || !token.trim()} onClick={onSubmit}>
          {busy ? '验证中' : '进去'}
        </Pressable>

        <Pressable className="cta-quiet mt-2 w-full text-[13px]" onClick={onRetry}>
          换一个口令 / 重新填
        </Pressable>
      </div>
    </div>
  );
}
